import * as Sentry from '@sentry/react';
import { DateTime } from '@reynolds/datetime';
import { OpType } from 'src/models';
import { IAppointmentState } from 'src/store/states';

type ServiceInfo = {
    readonly title: string;
    readonly category: string;
    readonly msrp: string;
};

type SdEventName =
    | 'scheduleServiceImpression'
    | 'scheduleServiceStart'
    | 'scheduleServiceLogin'
    | 'scheduleServiceVehicleShown'
    | 'scheduleServiceVehicleStart'
    | 'scheduleServiceVehicleIndicated'
    | 'scheduleServiceSelectionShown'
    | 'scheduleServiceSelectionStart'
    | 'scheduleServiceSelectionFinish'
    | 'scheduleServiceTransportShown'
    | 'scheduleServiceTransportStart'
    | 'scheduleServiceTransportFinish'
    | 'scheduleServiceApptShown'
    | 'scheduleServiceApptStart'
    | 'scheduleServiceApptSet'
    | 'scheduleServiceFormShown'
    | 'scheduleServiceFormStart'
    | 'scheduleServiceFormFinish'
    | 'scheduleServiceReviewShown'
    | 'scheduleServiceReviewStart'
    | 'scheduleServiceReviewFinish'
    | 'scheduleServiceAccountCreated';

type SdEventPayloads = {
    scheduleServiceImpression: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
    scheduleServiceStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
    scheduleServiceLogin: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
    scheduleServiceVehicleShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
    scheduleServiceVehicleStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };

    scheduleServiceVehicleIndicated: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
    };

    scheduleServiceSelectionShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
    };

    scheduleServiceSelectionStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
    };

    scheduleServiceSelectionFinish: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceTransportShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceTransportType: string;
    };
    scheduleServiceTransportStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceTransportType: string;
    };
    scheduleServiceTransportFinish: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceTransportType: string;
    };

    scheduleServiceApptShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceApptStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceApptSet: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceFormShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
    };

    scheduleServiceFormStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };
    
    scheduleServiceFormFinish: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceReviewShown: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };
    scheduleServiceReviewStart: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };
    scheduleServiceReviewFinish: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
        scheduleServiceVehicleYear: string;
        scheduleServiceVehicleMake: string;
        scheduleServiceVehicleModel: string;
        scheduleServiceVehicleVin: string;
        scheduleServiceVehicleMileage: string | number;
        scheduleServiceApptDate: string;
        scheduleServiceApptTime: string;
        scheduleServiceServices: ServiceInfo[];
    };

    scheduleServiceAccountCreated: {
        scheduleServiceSessionId: string,
        scheduleServiceProvider: string,
    };
};

export const getSdMileage = (mileage: number): string | number =>
    mileage === -1 || mileage === 0 ? '' : mileage;

export const buildApptData = (sessionId: string, providerId: string, appointment: IAppointmentState) => {
    const sdServices = appointment.ApptOps.map((op) => {
        let opCat = '';
        switch (op.opType) {
            case OpType.MaintenanceMenu:
                opCat = 'Scheduled Maintenance';
                break;
            case OpType.Recall:
                opCat = 'Recall';
                break;
            default:
                opCat = 'Repair';
                break;
        }
        let opPrice = '';
        if (op.itemCostHigh && op.itemCostHigh !== '****') {
            opPrice = op.itemCostHigh;
        } else if (op.itemCostLow && op.itemCostLow !== '****') {
            opPrice = op.itemCostLow;
        }

        return {
            title: op.concern || '',
            category: opCat,
            msrp: opPrice,
        };
    });

    const dateValue = new DateTime(new Date(appointment.Appointment.date));
    const time = appointment.Appointment.time.toUpperCase().replace(/^(\d{1,2}:\d{2})(AM|PM)$/, '$1 $2');

    return {
        scheduleServiceSessionId: sessionId,
        scheduleServiceProvider: providerId,
        scheduleServiceApptDate: dateValue.format('YYYY-MM-DD'),
        scheduleServiceApptTime: time,
        scheduleServiceVehicleMake: appointment.ApptVeh.make,
        scheduleServiceVehicleMileage: getSdMileage(appointment.ApptVeh.mileage),
        scheduleServiceVehicleModel: appointment.ApptVeh.model,
        scheduleServiceVehicleVin: appointment.ApptVeh.vin,
        scheduleServiceVehicleYear: appointment.ApptVeh.vehYr,
        scheduleServiceServices: sdServices,
    };
}

export const buildVehicleEventPayload = (
    sessionId: string,
    providerId: string,
    apptVeh: IAppointmentState['ApptVeh'],
) => ({
    scheduleServiceSessionId: sessionId,
    scheduleServiceProvider: providerId,
    scheduleServiceVehicleYear: apptVeh.vehYr,
    scheduleServiceVehicleMake: apptVeh.make,
    scheduleServiceVehicleModel: apptVeh.model,
    scheduleServiceVehicleVin: apptVeh.vin,
    scheduleServiceVehicleMileage: getSdMileage(apptVeh.mileage),
});

// The start events that should only fire once per session
const ONCE_PER_SESSION_EVENTS = new Set([
    'scheduleServiceStart',
    'scheduleServiceVehicleStart',
    'scheduleServiceSelectionShown',
    'scheduleServiceSelectionStart',
    'scheduleServiceTransportStart',
    'scheduleServiceApptStart',
    'scheduleServiceApptSet',
    'scheduleServiceReviewStart',
    'scheduleServiceFormShown',
    'scheduleServiceFormStart',
    'scheduleServiceFormFinish',
]);

function isOncePerSessionEvent(event: string): boolean {
    return ONCE_PER_SESSION_EVENTS.has(event);
}

export function getProviderId(
    specData,
    toyotaMCLaunched,
    toyotaTDMCLaunched,
    lexusMCLaunched,
    lexusTDMCLaunched
) {
    let providerId = '';

    if (specData?.toyotaTrackingFlag! === 'Y') {
        providerId =
            toyotaMCLaunched || toyotaTDMCLaunched
                ? 'SMARTPATHREYNOLDS'
                : 'REYNOLDS';
    } else if (specData?.lexusTrackingFlag! === 'Y') {
        providerId =
            lexusMCLaunched || lexusTDMCLaunched
                ? 'MONOGRAMREYNOLDS'
                : 'REYNOLDS';
    } else if (
        specData?.mbzTrackingFlag! === 'Y' ||
        specData?.mazdaCATrackingFlag! === 'Y' ||
        specData?.vwTrackingFlag! === 'Y' ||
        specData?.jaguarTrackingFlag! === 'Y' ||
        specData?.landRoverTrackingFlag! === 'Y'
    ) {
        providerId = 'REYNOLDS';
    }

    return providerId;
}

export const sendSdEvent = <K extends SdEventName>(event: K, data: SdEventPayloads[K], opts?: {oncePerSession?: boolean}) => {
    if (!navigator.cookieEnabled) return;
    const dedupe = isOncePerSessionEvent(event) || opts?.oncePerSession === true;
    const hasSessionStorage = typeof sessionStorage !== 'undefined';
    const key = `_sd_event_${event}`;
    // Single-fire session events: skip if already sent this session
    if (dedupe && hasSessionStorage && sessionStorage.getItem(key)) return;

    try {
        const payload = {
            ...data,
            events: event,
        };
        if (typeof window !== "undefined" && typeof window['sd'] === "function") {
            window['sd']('dataLayer', payload);
            const sendResult = window['sd']('send');
            if (dedupe && hasSessionStorage) {
                sessionStorage.setItem(key, '1');
            }
            if (sendResult && typeof sendResult.catch === 'function') {
                sendResult.catch((e) => Sentry.captureException(e));
            }
        }
    } catch (e) {
        Sentry.captureException(e);
    }
};
